import React, { useState } from 'react';
import { Lock, Phone } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import PortalBackground from '../PortalBackground';
import KickoffCallScheduler from './KickoffCallScheduler';
import PortalPrograms from '../../pages/PortalPrograms';
import { BBA_CHECK_IN_CALENDLY_URL } from '../../utils/calendlyWidget';

/**
 * Dashboard content for a locked portal (no active plan). Instead of a bare
 * lock card, the client gets the program list (embedded PortalPrograms — each
 * card's Learn More opens /portal/programdetail, which checks out while
 * locked) plus an inline check-in call scheduler, on the same ambient
 * background as the profile page.
 */
const PortalLockedDashboard = () => {
  const { darkMode } = useTheme();
  const [schedulerOpen, setSchedulerOpen] = useState(false);

  return (
    <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
      {/* Same ambient BBA background the profile page shows */}
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto w-full">
        <header className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white text-center">
            It Looks Like You Don't Have an Active Plan
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-center">
            Let's fix that — pick the program that fits you below, or book a quick check-in
            call and we'll help you choose.
          </p>
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={() => setSchedulerOpen((open) => !open)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors shadow"
            >
              <Phone className="w-4 h-4" />
              {schedulerOpen ? 'Hide Scheduler' : 'Book a Check-In Call'}
            </button>
          </div>
        </header>

        {schedulerOpen && (
          <div className="max-w-3xl mx-auto mb-10">
            <KickoffCallScheduler darkMode={darkMode} url={BBA_CHECK_IN_CALENDLY_URL} />
          </div>
        )}

        {/* Program cards + comparison — Learn More → /portal/programdetail */}
        <PortalPrograms embedded />
      </div>
    </div>
  );
};

export default PortalLockedDashboard;
